import { ParsedQuery } from '../api';

/**
 * What the service understood, shown next to what it returned.
 *
 * This panel is the demo's actual argument. Anyone can return rows for
 * "2 bedroom Rosebank"; the thing being sold is that "two bed near Rosebank under
 * 15k with a pool" became structured constraints without the tenant writing a
 * parser. Hiding that leaves a prospect looking at a list they assume is a SQL
 * LIKE.
 */
export default function ParsedPanel({ parsed, diagnostics }: { parsed: ParsedQuery; diagnostics?: Record<string, unknown> }) {
  const rows: Array<[string, string]> = [];

  const range = (r?: { min?: number; max?: number }, unit = '') => {
    if (!r) return null;
    if (r.min != null && r.max != null) return `${r.min}–${r.max}${unit}`;
    if (r.min != null) return `${r.min}+${unit}`;
    if (r.max != null) return `up to ${r.max}${unit}`;
    return null;
  };

  const beds = range(parsed.bedrooms);
  if (beds) rows.push(['bedrooms', beds]);

  const baths = range(parsed.bathrooms);
  if (baths) rows.push(['bathrooms', baths]);

  if (parsed.price) {
    const price = range(parsed.price);
    if (price) rows.push(['price', `${price} ${parsed.price.currency ?? ''}`.trim()]);
  }

  // Shown as separate rows on purpose: they are ANDed, not alternatives, and a
  // prospect reading "suburb Rosebank / city Cape Town" can see the
  // qualification the service applied.
  if (parsed.areas?.length) rows.push(['suburb', parsed.areas.join(' or ')]);
  if (parsed.cities?.length) rows.push(['city', parsed.cities.join(' or ')]);
  if (parsed.amenities?.length) rows.push(['amenities', parsed.amenities.join(', ')]);
  if (parsed.tags?.length) rows.push(['tags', parsed.tags.join(', ')]);
  if (parsed.commute) {
    rows.push(['commute', `≤ ${parsed.commute.maxMinutes} min to ${parsed.commute.label} by ${parsed.commute.mode}`]);
  }
  if (parsed.freeText) rows.push(['free text', parsed.freeText]);

  // The words the parser could not turn into a constraint still drive the vector
  // search, so an empty panel is a legitimate outcome, not a failure. Saying so
  // beats rendering nothing and letting a prospect conclude it is broken.
  // `diagnostics` is an open Record<string, unknown> on the wire — the service
  // documents it as "how the query was understood", not as a fixed shape — so
  // each field is narrowed here rather than trusted.
  // `numericSource` is what search-service actually returns — which path
  // extracted the numeric constraints, 'llm' or the regex fast path.
  const rawSource = diagnostics?.numericSource;
  const source = typeof rawSource === 'string' ? rawSource : null;
  const rawMs = diagnostics?.durationMs;
  const tookMs = typeof rawMs === 'number' && Number.isFinite(rawMs) ? Math.round(rawMs) : null;

  return (
    <div className="card">
      <h3>How the query was understood</h3>
      {rows.length === 0 ? (
        <p className="footnote" style={{ marginTop: 0 }}>
          No hard constraints were extracted — the whole phrase was matched semantically against the index.
        </p>
      ) : (
        <div className="parsed">
          {rows.map(([k, v]) => (
            <div className="parsed-row" key={k}>
              <span className="k">{k}</span>
              <span className="v">{v}</span>
            </div>
          ))}
        </div>
      )}
      {(source || tookMs != null) && (
        <p className="footnote">
          {source ? `constraints from ${source}` : null}
          {source && tookMs != null ? ' · ' : null}
          {tookMs != null ? `${tookMs} ms` : null}
        </p>
      )}
    </div>
  );
}
